/**
 * Gestion du cookie de session `mp_session`.
 *
 * Le cookie porte un UUID généré côté client au premier chargement.
 * Il sert de clé de sauvegarde côté serveur — rien de sensible dedans.
 * SameSite=Lax suffit : on n'a pas d'action cross-site à protéger.
 */

const COOKIE_NAME = "mp_session";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 an

function readCookie(name: string): string | null {
    const entry = document.cookie
        .split(";")
        .map((p) => p.trim())
        .find((p) => p.startsWith(`${name}=`));
    if (!entry) return null;
    return decodeURIComponent(entry.slice(name.length + 1));
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
    const parts = [
        `${name}=${encodeURIComponent(value)}`,
        `path=/`,
        `max-age=${maxAgeSeconds}`,
        `samesite=lax`,
    ];
    if (window.location.protocol === "https:") parts.push("secure");
    document.cookie = parts.join("; ");
}

function expireCookie(name: string): void {
    document.cookie = `${name}=; path=/; max-age=0; samesite=lax`;
}

function isValidUuid(candidate: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate);
}

/** Retourne l'UUID de session, en le générant (et en le posant) s'il n'existe pas. */
export function getOrCreateSessionId(): string {
    const existing = readCookie(COOKIE_NAME);
    if (existing && isValidUuid(existing)) return existing;
    const fresh = crypto.randomUUID();
    writeCookie(COOKIE_NAME, fresh, COOKIE_MAX_AGE_SECONDS);
    return fresh;
}

/** Supprime le cookie de session — usage : bouton « nouvelle partie ». */
export function clearSession(): void {
    expireCookie(COOKIE_NAME);
}
